import {
  useLpSettlementBatchQuery,
  useLpSettlementInstructionsQuery,
} from '@src/providers/AppStateProvider/streamingDataSlice';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import type { AgGridMenuItems, BlotterTableSettings, LPSettlementBatchStatusEnum } from '@talos/kyoko';
import {
  ACTION,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  CollapseAllButton,
  columnTypes,
  ExpandAllButton,
  FormControlSizes,
  IconName,
  LPSettlementInstructionStatusEnum,
  MixpanelEvent,
  MixpanelEventSource,
  SettlementInstructionDirectionEnum,
  Toggle,
  useBehaviorSubject,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useMixpanel,
  usePersistedBlotterTable,
  type BlotterTableClientLocalFilter,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useRoleAuth } from '../../../../hooks';
import { useTradingSettings } from '../../../../providers/TradingSettingsContext';
import type {
  LPSettlementBatchMessage,
  LPSettlementBatchMessageData,
  LPSettlementInstructionMessage,
} from '../generated/lpSettlementTypes';
import { useLPSettlementContext } from '../LPSettlementProvider';
import { useSettleBatchContext } from '../SettleBatchContext';
import { LP_SETTLEMENT_BLOTTER_PORTAL_ID } from '../tokens';
import { isBatchLocked } from '../utils/lpBatchUtils';
import {
  buildBatchDetailsOpenedEventProperties,
  LpSettlementBatchDetailsOpenedFrom,
} from '../utils/lpSettlementMixpanelUtils';
import { useLPSettlementBatchesGridColumns } from './useLPSettlementBatchesGridColumns';

// Use the actual message data type instead of the old Batch/BatchTransfer types
type LPSettlementBatchData = LPSettlementBatchMessage['data'][0];
type LPSettlementInstructionData = LPSettlementInstructionMessage['data'][0];

// Utility type to prefix all properties of a type with a given prefix
type Prefixed<T, P extends string> = {
  [K in keyof T as `${P}${Capitalize<string & K>}`]: T[K];
};

// Row that represents an instruction with its batch data
export type LPSettlementInstructionRow = LPSettlementInstructionData &
  Omit<Prefixed<LPSettlementBatchData, 'Batch'>, 'BatchBatchID'> & {
    // For row ID uniqueness
    rowID: string;
  };

type LPSettlementBatchesGridMode = 'activeBatches' | 'batchHistory';

interface LPSettlementBatchesGridProps {
  /** Unique blotter ID for persistence */
  blotterID?: string;
  /** Status filters (optional) - for batches */
  BatchStatuses?: LPSettlementBatchStatusEnum[];
  /** Start date filter (optional) - defaults to 7 days ago if no other filters */
  StartDate?: string;
  /** End date filter (optional) */
  EndDate?: string;
  /** Export filename (e.g., "Active Batches", "Batch History") */
  exportName?: string;
  /** Grid mode: 'activeBatches' or 'batchHistory' */
  mode: LPSettlementBatchesGridMode;
}

// columnTypes.group ensures all cell renderers are consistent with columns
const autoGroupColumnDef = columnTypes.group({
  type: 'group',
  title: 'Grouping',
  editable: false,
  width: 300,
  pinned: 'left',
  hide: false,
  sortable: true,
  suppressColumnsToolPanel: false,
  params: {
    suppressCount: true,
  },
});

/** Common dynamic-grouping-enabled blotter for LP Settlement Batch analysis */
export function LPSettlementBatchesGrid({
  blotterID = 'lp-settlement-batches',
  BatchStatuses,
  StartDate,
  EndDate,
  exportName = 'Settlement Batches',
  mode,
}: LPSettlementBatchesGridProps) {
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const { openBatchDetails } = useSettleBatchContext();
  const mixpanel = useMixpanel(MixpanelEventSource.PortfolioLpSettlement);
  const { isAuthorized } = useRoleAuth();
  const hasLPSettlementRole = isAuthorized(ACTION.EDIT_LP_SETTLEMENT);
  const { openUnbatchDialog, showAllDecimals, setShowAllDecimals } = useLPSettlementContext();

  // Quick filter state
  const [quickFilterText, setQuickFilterText] = useState('');

  const getCustomContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<LPSettlementInstructionRow>) => {
      const items: AgGridMenuItems<LPSettlementInstructionRow> = [];

      const node = params.node;
      const isBatchLevelGroup = node?.group && node.field === 'SettleBatchID';

      if (isBatchLevelGroup) {
        const batchData = node.allLeafChildren?.[0]?.data;
        const batchId = batchData?.SettleBatchID;
        if (batchData && batchId) {
          const settlementBatch = rowDataToBatchMessageData(batchData);
          const isLocked = settlementBatch ? isBatchLocked(settlementBatch) : false;

          items.push({
            name: 'Open Details',
            icon: `<i class="ag-icon ${IconName.Deepdive}"/>`,
            action: () => {
              mixpanel.track(
                MixpanelEvent.LPSettlementBatchDetails,
                buildBatchDetailsOpenedEventProperties({
                  openedFrom: LpSettlementBatchDetailsOpenedFrom.GridMenu,
                  status: settlementBatch?.Status,
                  creationSource: settlementBatch?.CreationSource,
                })
              );
              openBatchDetails({ batchID: batchId });
            },
          });

          if (!isLocked && settlementBatch) {
            items.push({
              name: 'Unbatch',
              icon: `<i class="ag-icon ${IconName.Trash}"/>`,
              action: () => {
                openUnbatchDialog(settlementBatch);
              },
            });
          }
        }

        return compact(items);
      }

      if (!node?.group) {
        const rowData = node?.data;
        if (!rowData) {
          return [];
        }

        const batchId = rowData.SettleBatchID;
        const instructionId = rowData.InstructionID;

        if (
          hasLPSettlementRole &&
          rowData.Status === LPSettlementInstructionStatusEnum.Error &&
          batchId &&
          instructionId
        ) {
          const recoveryActionName =
            rowData.Direction === SettlementInstructionDirectionEnum.Outbound ? 'Retry Transfer' : 'Update';

          items.push({
            name: recoveryActionName,
            action: () => {
              mixpanel.track(
                MixpanelEvent.LPSettlementBatchDetails,
                buildBatchDetailsOpenedEventProperties({
                  openedFrom: LpSettlementBatchDetailsOpenedFrom.ErrorRetry,
                  status: rowData.BatchStatus,
                  creationSource: rowData.BatchCreationSource,
                })
              );
              openBatchDetails({ batchID: batchId, instructionID: instructionId });
            },
          });
        }

        if (batchId) {
          items.push({
            name: 'Open Details',
            action: () => {
              mixpanel.track(
                MixpanelEvent.LPSettlementBatchDetails,
                buildBatchDetailsOpenedEventProperties({
                  openedFrom: LpSettlementBatchDetailsOpenedFrom.GridMenu,
                  status: rowData.BatchStatus,
                  creationSource: rowData.BatchCreationSource,
                })
              );
              openBatchDetails({ batchID: batchId });
            },
          });
        }
      }

      return compact(items);
    },
    [hasLPSettlementRole, mixpanel, openBatchDetails, openUnbatchDialog]
  );

  // Use the streaming data endpoint with provided filters
  const { data: batches } = useLpSettlementBatchQuery({
    Statuses: BatchStatuses,
    StartDate,
    EndDate,
  });

  const { homeCurrency } = useDisplaySettings();
  // Always fetch instructions (without status filter - we'll filter by batch data)
  const { data: instructions } = useLpSettlementInstructionsQuery({
    StartDate,
    EndDate,
    EquivalentCurrency: homeCurrency,
    BatchStatuses,
  });

  const { columns, aggFuncs } = useLPSettlementBatchesGridColumns(openBatchDetails, mode);

  // Set up persistence for draggable groupings
  const persistedBlotterTable = usePersistedBlotterTable(blotterID);

  const data = useMemo<LPSettlementInstructionRow[]>(() => {
    if (!batches || !instructions) {
      return [];
    }

    // Create a map of batches by BatchID for easy lookup
    const batchMap = new Map(Array.from(batches.values()).map(batch => [batch.BatchID, batch]));

    // Create one row per instruction
    const rows: LPSettlementInstructionRow[] = [];
    for (const instruction of instructions.values()) {
      const batch = batchMap.get(instruction.SettleBatchID);
      if (batch) {
        rows.push({
          // Instruction fields
          ...instruction,
          // Batch fields (prefixed to avoid conflicts)
          ...prefixBatchData(batch),
          // Unique row ID
          rowID: `${instruction.SettleBatchID}-${instruction.InstructionID}`,
        });
      }
    }

    return rows;
  }, [batches, instructions]);

  const { observable: dataObservable } = useBehaviorSubject(() => ({ initial: true, data }), [data]);

  // Client-side filter to handle batch status changes from streaming updates
  // When a batch status changes (e.g., 'Created' -> 'Settled'), streaming updates
  // will update the cache but won't remove it from the view. This filter ensures
  // rows are filtered out if their batch status no longer matches the criteria.
  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<LPSettlementInstructionRow>>(
    row => {
      const data = row.data;
      if (!data) {
        return false;
      }

      // If BatchStatuses filter is provided, only include rows where the batch status matches
      if (BatchStatuses && BatchStatuses.length > 0) {
        if (!data.BatchStatus || !BatchStatuses.includes(data.BatchStatus)) {
          return false;
        }
      }

      return true;
    },
    [BatchStatuses]
  );

  const blotterTableSettings = useMemo(
    (): BlotterTableSettings => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const blotterTable = useBlotterTable<LPSettlementInstructionRow>({
    columns,
    rowID: 'rowID',
    dataObservable,
    errorObservable: undefined,
    persistence: persistedBlotterTable,
    settings: blotterTableSettings,
    clientLocalFilter: clientSideFilter,
    quickSearchParams: {
      filterText: quickFilterText,
    },
    contextMenuOptions: { getCustomContextMenuItems },
    gridOptions: {
      groupHideParentOfSingleChild: 'leafGroupsOnly',
      autoGroupColumnDef,
      rowGroupPanelShow: 'always',
      groupDisplayType: 'singleColumn',
      aggFuncs,
      onRowDoubleClicked: ({ data, node }) => {
        // Only handle double-click for leaf rows (not group rows)
        if (data && !node.group) {
          mixpanel.track(
            MixpanelEvent.LPSettlementBatchDetails,
            buildBatchDetailsOpenedEventProperties({
              openedFrom: LpSettlementBatchDetailsOpenedFrom.RowDoubleClick,
              status: data.BatchStatus,
              creationSource: data.BatchCreationSource,
            })
          );
          openBatchDetails({ batchID: data.SettleBatchID });
        }
      },
    },
    exportOptions: {
      name: exportName,
    },
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;
  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        portalId={LP_SETTLEMENT_BLOTTER_PORTAL_ID}
        showQuickFilter={true}
        showFilterBuilder={false}
        showDateRangePicker={false}
        quickFilterText={quickFilterText}
        onQuickFilterTextChanged={setQuickFilterText}
        prefix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} />
            {!showAllDecimalsInBlotters && (
              <BlotterTableExtrasMenu dataTestID="recon-overview-extras-menu" {...extrasMenuPopover}>
                <Toggle
                  size={FormControlSizes.Small}
                  checked={showAllDecimals}
                  onChange={setShowAllDecimals}
                  label="Show All Decimals"
                  data-testid="show-all-decimals-toggle"
                />
              </BlotterTableExtrasMenu>
            )}
          </>
        }
      />
      <BlotterTable {...blotterTable} />
    </>
  );
}

// Helper function to transform batch data with "Batch" prefix
function prefixBatchData(batch: LPSettlementBatchData): Omit<Prefixed<LPSettlementBatchData, 'Batch'>, 'BatchBatchID'> {
  return {
    BatchRevision: batch.Revision,
    BatchCreationSource: batch.CreationSource,
    BatchReportCount: batch.ReportCount,
    BatchInstructionCount: batch.InstructionCount,
    BatchSettlementSubAccounts: batch.SettlementSubAccounts,
    BatchSettleDeadline: batch.SettleDeadline,
    BatchSettleCompletion: batch.SettleCompletion,
    BatchTimestamp: batch.Timestamp,
    BatchTransactTime: batch.TransactTime,
    BatchCounterparty: batch.Counterparty,
    BatchCompletedInstructionCount: batch.CompletedInstructionCount,
    BatchPercentCompleted: batch.PercentCompleted,
    BatchCreationUser: batch.CreationUser,
    BatchMarketAccount: batch.MarketAccount,
    BatchStatus: batch.Status,
  };
}

// Helper function to convert LPSettlementInstructionRow (with prefixed batch data) to LPSettlementBatchMessageData
function rowDataToBatchMessageData(row: LPSettlementInstructionRow): LPSettlementBatchMessageData | null {
  if (!row.SettleBatchID || row.BatchRevision === undefined) {
    return null;
  }

  return {
    BatchID: row.SettleBatchID,
    Revision: row.BatchRevision,
    Status: row.BatchStatus,
    Counterparty: row.BatchCounterparty,
    MarketAccount: row.BatchMarketAccount,
    CreationUser: row.BatchCreationUser,
    CreationSource: row.BatchCreationSource,
    ReportCount: row.BatchReportCount,
    InstructionCount: row.BatchInstructionCount,
    CompletedInstructionCount: row.BatchCompletedInstructionCount,
    PercentCompleted: row.BatchPercentCompleted,
    SettleDeadline: row.BatchSettleDeadline,
    SettleCompletion: row.BatchSettleCompletion,
    SettlementSubAccounts: row.BatchSettlementSubAccounts,
    Timestamp: row.BatchTimestamp,
    TransactTime: row.BatchTransactTime,
    Comments: undefined, // Not available in row data
  } as LPSettlementBatchMessageData;
}
